import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cross2Icon, TrashIcon } from '@radix-ui/react-icons';
import { useMsal } from '@azure/msal-react';
import { useDispatch } from 'react-redux';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { PromptLibraryCard } from './PromptLibraryCard';
import { PromptLibraryFilters } from './PromptLibraryFilters';
import { useUserCreatedPrompts } from './useSavedPrompts';
import { getAllPrompts, getAgentTypeLabel } from './promptLibraryData';
import { PromptAgentType, PromptLibraryItem, UserCreatedPrompt } from './types';
import { BookmarkPromptModal } from './BookmarkPromptModal';
import { setChatMode, setRagChatType, setPromptFromLibrary, type RagChatType } from '../../store/chatModeSlice';
import FeedbackToast from '../../components/Chat/feedback/FeedbackToast';
import { ConfirmationDialog } from '../../components/Dialogs/ConfirmationDialog';

interface PromptLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (prompt: string, tool?: string, agentType?: PromptAgentType) => void;
}

type Tab = 'suggested' | 'saved';

export const PromptLibrary: React.FC<PromptLibraryProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const { instance: msalInstance } = useMsal();
  const dispatch = useDispatch();

  const [selectedAgentTypes, setSelectedAgentTypes] = useState<PromptAgentType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('saved');
  const [toastState, setToastState] = useState<{ show: boolean; message: string; variant: 'success' | 'error' }>({ show: false, message: '', variant: 'success' });
  const { userPrompts, isLoading, fetchUserPrompts, saveUserPrompt, updateUserPrompt, deleteUserPrompt } = useUserCreatedPrompts();
  const isSaved = useCallback((prompt: string) => userPrompts.some((p) => p.prompt === prompt), [userPrompts]);
  const [editingPrompt, setEditingPrompt] = useState<{ id: string; title: string; prompt: string; agentType: PromptAgentType } | null>(null);
  const [isAddPromptModalOpen, setIsAddPromptModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ id: string; title: string } | null>(null);

  const showToast = (message: string, variant: 'success' | 'error') =>
    setToastState({ show: true, message, variant });

  const handleDeleteConfirmed = async () => {
    if (!deleteConfirmation || !msalInstance) return;
    const { id } = deleteConfirmation;
    setDeleteConfirmation(null);
    try {
      await deleteUserPrompt(msalInstance, id);
      showToast('Prompt deleted.', 'success');
    } catch {
      showToast('Failed to delete prompt. Please try again.', 'error');
    }
  };

  const handleToggleSaved = async (item: PromptLibraryItem) => {
    const wasSaved = isSaved(item.prompt);
    if (!wasSaved) {
      if (msalInstance) {
        try {
          await saveUserPrompt(msalInstance, {
            title: item.title,
            prompt: item.prompt,
            agentType: item.agentType,
            category: item.category,
          });
          showToast('Prompt bookmarked! Find it in "My saved prompts".', 'success');
        } catch {
          showToast('Failed to bookmark prompt. Please try again.', 'error');
        }
      }
    } else {
      if (msalInstance) {
        const matching = userPrompts.find((p) => p.prompt === item.prompt);
        if (matching?.id) {
          try {
            await deleteUserPrompt(msalInstance, matching.id);
            showToast('Prompt removed from saved.', 'success');
          } catch {
            showToast('Failed to remove prompt. Please try again.', 'error');
          }
        }
      }
    }
  };

  // Fetch user-created prompts once on first open (subsequent opens use cached data)
  useEffect(() => {
    if (isOpen && msalInstance) {
      fetchUserPrompts(msalInstance);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const allPrompts = useMemo(() => getAllPrompts(), []);

  const filteredPrompts = useMemo(() => {
    let items: PromptLibraryItem[] = allPrompts;

    // Filter by tab
    if (activeTab === 'saved') {
      items = userPrompts.map((up) => ({
        title: up.title,
        prompt: up.prompt,
        agentType: up.agentType,
        _userCreatedId: up.id,
      } as PromptLibraryItem & { _userCreatedId?: string }));
    }

    // Filter by chat types (empty array means all)
    if (selectedAgentTypes.length > 0) {
      items = items.filter((p) => selectedAgentTypes.includes(p.agentType));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((p) =>  p.title.toLowerCase().includes(q) || p.prompt.toLowerCase().includes(q));
    }

    return items;
  }, [allPrompts, activeTab, selectedAgentTypes, searchQuery, userPrompts]);

  const handleSelect = (prompt: string, tool?: string, agentType?: PromptAgentType) => {
    // If parent provided an onSelect callback, delegate to it (e.g. for confirmation flow)
    if (onSelect) {
      onSelect(prompt, tool, agentType);
      return;
    }
    // Dispatch prompt to Redux so ChatInput can consume it
    dispatch(setPromptFromLibrary({ text: prompt, tool }));
    // Switch RAG mode if selecting a non-Ask-Chat agent
    if (agentType && agentType !== 'Ask Chat') {
      dispatch(setRagChatType(agentType as RagChatType));
    } else {
      dispatch(setChatMode('Ask Chat'));
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="prompt-library-overlay"
          initial={{ opacity: 0, scale: 0.93 }}
          animate={{ opacity: 1, scale: 1, transition: { duration: 0.25, ease: [0.0, 0.0, 0.2, 1] } }}
          exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.25, ease: [0.4, 0.0, 0.2, 1] } }}
          className="absolute inset-0 flex flex-col overflow-hidden"
          style={{
            zIndex: 100,
            backgroundColor: 'var(--main-background)',
            transformOrigin: 'bottom left',
            willChange: 'transform, opacity',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 pt-4 pb-0 flex-shrink-0 bg-[#202024]"
            style={{
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <h1
              className="text-xl font-bold pb-4 text-white"
              style={{
                fontFamily: '3MCircularTT, sans-serif',
              }}
            >
              Saved Prompts
            </h1>


            <div className="flex items-end gap-5">
              {/* Tab links - commented out for R7 release (single tab only) */}
              {/* <button
                className={`text-md pb-3 px-2 border-b-4 transition-colors ${
                  activeTab === 'saved'
                    ? 'border-[#5E95ED] text-[#5E95ED] font-semibold'
                    : 'border-transparent text-[#a1a1aa]'
                }`}
                onClick={() => setActiveTab('saved')}
              >
                My saved prompts
              </button>
              <button
                className={`text-md pb-3 px-2 border-b-4 transition-colors ${
                  activeTab === 'suggested'
                    ? 'border-[#5E95ED] text-[#5E95ED] font-semibold'
                    : 'border-transparent text-[#a1a1aa]'
                }`}
                onClick={() => setActiveTab('suggested')}
              >
                Suggested prompts
              </button> */}

              {/* Close button */}
              <button
                className="p-1.5 mb-3 rounded-md transition-colors hover:bg-black/10 dark:hover:bg-white/10"
                onClick={onClose}
                title="Close"
              >
                <Cross2Icon
                  width={20}
                  height={20}
                  className="text-[#a1a1aa]"
                />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="px-6 py-4 flex-shrink-0">
            <PromptLibraryFilters
              selectedAgentTypes={selectedAgentTypes}
              onSelectedAgentTypesChange={setSelectedAgentTypes}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              beforeSearch={activeTab === 'saved' ? (
                <button
                  type="button"
                  onClick={() => setIsAddPromptModalOpen(true)}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-all duration-200 flex-shrink-0 bg-[#5E95ED] border-[#5E95ED] text-white hover:bg-[#4a82d8] hover:border-[#4a82d8]"
                  title="Add new prompt"
                  aria-label="Add new prompt"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </button>
              ) : undefined}
            />
          </div>

          {/* Card grid */}
          <div
            className="flex-1 overflow-y-auto px-6 pb-6 scrollbar-hide flex flex-col"
            style={{ scrollBehavior: 'smooth', paddingTop: '8px', marginTop: '-8px' }}
          >
            {activeTab === 'saved' && isLoading ? (
              <div
                className="flex flex-col items-center gap-3 pt-12"
                style={{
                  color: isDark ? '#a1a1aa' : '#6b7280',
                  fontFamily: '3MCircularTT, sans-serif',
                }}
              >
                <svg width={24} height={24} viewBox="0 0 16 16" fill="none" className="animate-spin">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeOpacity="0.25" />
                  <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <p className="text-sm">Loading saved prompts...</p>
              </div>
            ) : filteredPrompts.length === 0 ? (
              <div
                className="flex flex-col items-center pt-12"
                style={{
                  color: isDark ? '#71717a' : '#9ca3af',
                  fontFamily: '3MCircularTT, sans-serif',
                }}
              >
                <p className="text-sm">
                  {activeTab === 'saved'
                    ? 'No saved prompts yet. Bookmark prompts to see them here.'
                    : 'No prompts match your filters.'}
                </p>
              </div>
            ) : (
              <div>
                {selectedAgentTypes.length > 0 ? (
                  selectedAgentTypes.map((agentType) => {
                    const agentItems = filteredPrompts.filter((p) => p.agentType === agentType);
                    if (agentItems.length === 0) return null;
                    return (
                      <div key={agentType} className="mb-6">
                        <h2
                          className="text-sm font-semibold uppercase tracking-wider mb-3 px-1"
                          style={{
                            color: isDark ? '#a1a1aa' : '#6b7280',
                            fontFamily: '3MCircularTT, sans-serif',
                          }}
                        >
                          {getAgentTypeLabel(agentType)}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {agentItems.map((item, idx) => {
                            const userCreatedId = (item as any)._userCreatedId as string | undefined;
                            return (
                              <PromptLibraryCard
                                key={userCreatedId || `${item.agentType}-${idx}`}
                                item={item}
                                isSaved={isSaved(item.prompt)}
                                onToggleSaved={() => handleToggleSaved(item)}
                                onClick={(prompt, tool) => handleSelect(prompt, tool, item.agentType)}
                                isUserCreated={!!userCreatedId}
                                onDelete={userCreatedId ? () => setDeleteConfirmation({ id: userCreatedId, title: item.title }) : undefined}
                                onEdit={userCreatedId ? () => {
                                  setEditingPrompt({ id: userCreatedId, title: item.title, prompt: item.prompt, agentType: item.agentType });
                                } : undefined}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredPrompts.map((item, idx) => {
                  const userCreatedId = (item as any)._userCreatedId as string | undefined;
                  return (
                    <PromptLibraryCard
                      key={userCreatedId || `${item.agentType}-${idx}`}
                      item={item}
                      isSaved={isSaved(item.prompt)}
                      onToggleSaved={() => handleToggleSaved(item)}
                      onClick={(prompt, tool) => handleSelect(prompt, tool, item.agentType)}
                      isUserCreated={!!userCreatedId}
                      onDelete={userCreatedId ? () => setDeleteConfirmation({ id: userCreatedId, title: item.title }) : undefined}
                      onEdit={userCreatedId ? () => {
                        setEditingPrompt({ id: userCreatedId, title: item.title, prompt: item.prompt, agentType: item.agentType });
                      } : undefined}
                    />
                  );
                })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Toast notifications for CRUD operations */}
          <div className="absolute bottom-6 inset-x-6" style={{ zIndex: 200, pointerEvents: 'none' }}>
            <div style={{ pointerEvents: 'auto' }}>
              <FeedbackToast
                show={toastState.show}
                onDismiss={() => setToastState((s) => ({ ...s, show: false }))}
                message={toastState.message}
                variant={toastState.variant}
              />
            </div>
          </div>

          {/* Add New Prompt Modal */}
          <BookmarkPromptModal
            isOpen={isAddPromptModalOpen}
            onClose={() => setIsAddPromptModalOpen(false)}
            onSave={async (newPrompt: Omit<UserCreatedPrompt, 'id' | 'createdAt'>) => {
              if (msalInstance) {
                try {
                  await saveUserPrompt(msalInstance, newPrompt);
                  showToast('Prompt saved successfully.', 'success');
                } catch {
                  showToast('Failed to save prompt. Please try again.', 'error');
                }
              }
              setIsAddPromptModalOpen(false);
            }}
          />

          {/* Edit Prompt Modal */}
          <BookmarkPromptModal
            isOpen={!!editingPrompt}
            onClose={() => setEditingPrompt(null)}
            onSave={async (updated: Omit<UserCreatedPrompt, 'id' | 'createdAt'>) => {
              if (editingPrompt && msalInstance) {
                try {
                  await updateUserPrompt(msalInstance, editingPrompt.id, updated);
                  showToast('Prompt updated successfully.', 'success');
                } catch {
                  showToast('Failed to update prompt. Please try again.', 'error');
                }
              }
              setEditingPrompt(null);
            }}
            initialTitle={editingPrompt?.title}
            initialDescription={editingPrompt?.prompt}
            initialAgentType={editingPrompt?.agentType}
            isEditMode
          />

          {/* Delete Confirmation Dialog */}
          <ConfirmationDialog
            isOpen={!!deleteConfirmation}
            title="Delete"
            message={`Are you sure you want to delete '${deleteConfirmation?.title}' prompt?`}
            confirmText="Delete"
            cancelText="Cancel"
            confirmButtonColor="#dc2626"
            confirmButtonHoverColor="#b91c1c"
            confirmIcon={<TrashIcon width={14} height={14} />}
            onConfirm={handleDeleteConfirmed}
            onCancel={() => setDeleteConfirmation(null)}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
